import { useEffect, useRef, useState } from 'react';
import { SCREEN_SHELL } from './kit';
import type { QueueState } from '../engine/queue';
import type { Task } from '../engine/session';
import { expr } from './format';

interface Props {
  queue: QueueState;
  task: Task;
  progress: number;
  onAnswer: (given: unknown, isCorrect: boolean) => void;
  onNext: () => void;
  onExit: () => void;
  /** Экран замирает целиком: автопереход не срабатывает и ответить нельзя.
   *  Нужен, пока поверх висит диалог. Без остановки автоперехода очередь
   *  уезжает под подложкой; без блокировки кнопок ребёнок с клавиатуры
   *  доходит табом до кнопки ответа за диалогом и отвечает на задание,
   *  которого не видит, — а время ответа при этом отсчитывается с момента
   *  до открытия диалога, то есть попытка ложится в журнал как очень
   *  медленная и рушит освоенность. */
  paused?: boolean;
}

/** Верное значение задания — его подсвечивают после ошибки (решение (k)). */
function correctOf(task: Task): unknown {
  if (task.type === 'choice4') return task.fact.answer;
  if (task.type === 'truefalse') return task.presented === task.fact.answer;
  return task.relation;
}

type Mark = 'neutral' | 'right' | 'wrong';

const ANSWER =
  'relative flex min-h-16 items-center justify-center gap-2 rounded-[0.85rem] border-2 px-2 py-5 text-2xl font-semibold tabular-nums transition-transform duration-100 active:not-disabled:scale-[0.97] disabled:cursor-default';
const TONE: Record<Mark, string> = {
  neutral: 'border-line bg-card text-ink',
  right: 'border-correct bg-correct-soft text-correct',
  wrong: 'border-incorrect bg-incorrect-soft text-incorrect',
};

/** Значок и подпись рядом с цветом, а не вместо него (§10). На цвет как
 *  единственный канал полагаться нельзя: у восьми процентов мальчиков
 *  зелёный и красный сливаются, и после ошибки такой ребёнок не отличает
 *  верный ответ от собственного промаха — обратная связь исчезает целиком. */
const SPOKEN_MARK: Record<Mark, string> = {
  neutral: '',
  right: ' — верный ответ',
  wrong: ' — твой ответ',
};

function AnswerButton({
  mark,
  disabled,
  onClick,
  text,
  spoken,
}: {
  mark: Mark;
  disabled: boolean;
  onClick: () => void;
  /** Что написано на кнопке. */
  text: string;
  /** Чем заменить надпись для диктора: знаки `>` `=` `<` он читает как
   *  «кнопка, кнопка, кнопка», и решить сравнение на слух нельзя. */
  spoken?: string;
}) {
  /** Подпись собирается целиком здесь, а не склеивается из соседних узлов:
   *  вычисление доступного имени обрезает пробелы по краям каждого узла, и
   *  «42» с «— верный ответ» слипались в «42— верный ответ». */
  return (
    <button
      type="button"
      aria-label={`${spoken ?? text}${SPOKEN_MARK[mark]}`}
      disabled={disabled}
      onClick={onClick}
      className={`${ANSWER} ${TONE[mark]}`}
    >
      {mark !== 'neutral' && (
        <span aria-hidden="true" className="text-xl">
          {mark === 'right' ? '✓' : '✕'}
        </span>
      )}
      <span aria-hidden="true">{text}</span>
    </button>
  );
}

const RELATION_LABEL: Record<string, string> = {
  '>': 'больше',
  '=': 'равно',
  '<': 'меньше',
};

export function TrainingScreen({ queue, task, progress, onAnswer, onNext, onExit, paused }: Props) {
  const correct = correctOf(task);
  /** Предусловие, и оно громкое.
   *
   *  `partner` объявлен в `Task` необязательным, а сравнение без второй
   *  части здесь не показать: два места ниже обращаются к нему через `!`,
   *  и экран падал с «Cannot read properties of undefined (reading
   *  'left')» — сообщением, по которому в отчёте Sentry не понять ничего.
   *  Третье место при этом было со сторожем, то есть половина кода считала
   *  состояние возможным, а половина нет.
   *
   *  Найдено мутационным проходом: сторож `type === 'compare' && partner`
   *  переживал подмену `&&` на `||`, потому что до него дело не доходило.
   *  Правило проекта: нарушенное предусловие — исключение с внятным
   *  текстом, а не молчаливое продолжение и не падение по дороге. */
  if (task.type === 'compare' && !task.partner) {
    throw new Error(`задание сравнения ${task.id} без второй части`);
  }

  const [given, setGiven] = useState<unknown>(null);
  const heading = useRef<HTMLDivElement>(null);
  /** Зеркало «что верно на экране» для обработчика нажатия: он объявлен
   *  выше самого значения. */
  const shownCorrectRef = useRef<unknown>(null);

  // Верный ответ: через секунду само следующее задание (§10 спеки).
  useEffect(() => {
    if (queue.phase !== 'feedbackCorrect' || paused) return;
    const timer = window.setTimeout(onNext, 900);
    return () => window.clearTimeout(timer);
  }, [queue.phase, onNext, paused]);

  /** Фокус переезжает на новое задание. Кнопки ответа снимаются вместе со
   *  старым заданием, и без этого фокус падает на `<body>`: ребёнок с
   *  клавиатурой каждый раз начинает обход экрана заново, а первой
   *  остановкой оказывается выход из тренировки. */
  useEffect(() => {
    heading.current?.focus();
  }, [task.id, queue.showIndex[task.id]]);

  const answer = (value: unknown) => {
    if (paused) return;
    if (queue.phase === 'awaiting') {
      setGiven(value);
      onAnswer(value, value === correct);
    } else if (queue.phase === 'guided' && value === shownCorrectRef.current) {
      /** Задание уже закрыто подсказкой: нажатие только подтверждает
       *  переход, и в `onAnswer` оно не уходит. Уйди оно туда — ребёнку
       *  записалась бы лишняя попытка, а по попыткам считается
       *  освоенность: подсказка начала бы улучшать статистику вместо того,
       *  чтобы оставлять её как есть.
       *
       *  Сравнение со значением — подстраховка, а не достижимая ветка, и
       *  довод здесь обязан быть ПОЛНЫМ: первая редакция объясняла только
       *  половину, и внешнее ревью справедливо на это указало.
       *
       *  Полностью так. До `else if` доходят фазы, кроме `awaiting` (её
       *  забирает ветка выше). В `feedbackCorrect` и `feedbackWrong` все
       *  кнопки ответа отключены через `frozen`, в `done` задания нет
       *  вовсе. Остаётся `guided`, где отключено всё, кроме верной кнопки.
       *  Поэтому подмена `&&` на `||` неразличима — не потому, что «в этой
       *  фазе кнопки отключены», а потому, что ДРУГИХ достижимых фаз у
       *  этой ветки нет.
       *
       *  ПОКАЗАНО, а не выведено: чужое значение пробовали подать прямым
       *  событием на отключённую кнопку — до обработчика оно не доходит,
       *  React отключённой кнопке клик не отдаёт. То есть достижимого пути
       *  к этой половине условия сейчас нет.
       *
       *  Стоит потому, что цена ошибки — испорченная освоенность, а цена
       *  строки — ничто, и потому что отключение кнопок — это одна правка
       *  вёрстки. Тест на попытку стоит рядом и покраснеет, если она
       *  случится. */
      setGiven(null);
      onNext();
    }
  };

  /** Ответ раскрывается СРАЗУ после ошибки, а не только после третьей
   *  (решение (k)): журнал в этот же момент записывает раскрытие, и экран
   *  обязан ему соответствовать. Пока здесь стояло одно лишь `guided`,
   *  ребёнок после ошибки видел «6 × 7 = 48» с зелёной кнопкой «Неверно» и
   *  так и не узнавал, что 6 × 7 = 42, — при том что освоенность уже
   *  считала ответ раскрытым и обнуляла замеры на 12 часов. */
  const revealed = queue.phase === 'feedbackWrong' || queue.phase === 'guided';

  /** Что верно ПО ТОМУ, ЧТО СЕЙЧАС НА ЭКРАНЕ.
   *
   *  В «верно/неверно» после раскрытия равенство переписано на истинное, и
   *  верной становится кнопка «Верно» — так прямо сказано в разделе 5.7.
   *  Пока это расходилось, экран показывал «6 × 7 = 42», ставил галочку на
   *  «Неверно» и в режиме подсказки не давал нажать ничего другого: выйти
   *  из задания можно было, только заявив, что 6 × 7 = 42 — ложь. Ровно
   *  тому, чему тренажёр учить не должен. */
  const shownCorrect = task.type === 'truefalse' && revealed ? true : correct;

  /** Переписано ли равенство под ребёнком. Только в этом случае пометки на
   *  кнопках лгут: показанное равенство истинно, поэтому зелёная галочка на
   *  «Неверно» — прямая ложь, а красный крест на «Верно» — тоже.
   *
   *  Когда предъявленное значение и так было верным (а это половина заданий
   *  этого типа), равенство не меняется, и обычные пометки совершенно
   *  правдивы. Гасить их там нельзя: экран после ответа становился
   *  неотличим от экрана до ответа, ребёнок не узнавал, что верной была
   *  «Верно», — а движок в этот же момент списывал с него 12 часов замеров
   *  по этому факту. Плата за обучение взималась, обучение не выдавалось. */
  const rewritten =
    task.type === 'truefalse' && revealed && task.presented !== task.fact.answer;

  const markOf = (value: unknown): Mark => {
    if (queue.phase === 'feedbackCorrect') return value === correct ? 'right' : 'neutral';
    if (queue.phase === 'feedbackWrong') {
      // Равенство переписано: пометки молчат, учит само равенство и подпись.
      if (rewritten) return 'neutral';
      if (value === given) return 'wrong';
      if (value === shownCorrect) return 'right';
      return 'neutral';
    }
    if (queue.phase === 'guided' && value === shownCorrect) return 'right';
    return 'neutral';
  };

  const frozen = queue.phase === 'feedbackCorrect' || queue.phase === 'feedbackWrong';
  const blocked = (value: unknown) =>
    Boolean(paused) || frozen || (queue.phase === 'guided' && value !== shownCorrect);
  const question = 'm-0 text-center text-[2.1rem] font-semibold tabular-nums';
  shownCorrectRef.current = shownCorrect;

  /** Раскрытое равенство словами. Для сравнения — оба, иначе ребёнок с
   *  диктором узнаёт половину того, за что уже заплатил обнулением
   *  замеров по двум фактам. */
  const revealedText =
    task.type === 'compare'
      ? `${expr(task.fact)} = ${task.fact.answer}, ${expr(task.partner!)} = ${task.partner!.answer}`
      : `${expr(task.fact)} = ${task.fact.answer}`;

  const announcement =
    queue.phase === 'feedbackCorrect'
      ? 'Верно'
      : queue.phase === 'feedbackWrong'
        ? `Неверно. ${revealedText}`
        : queue.phase === 'guided'
          ? `Нажми верный ответ. ${revealedText}`
          : '';

  return (
    <main className={SCREEN_SHELL}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onExit}
          aria-label="Выйти из тренировки"
          className="size-11 flex-none rounded-full bg-card text-[1.1rem] text-muted"
        >
          ✕
        </button>
        <div
          className="h-2 flex-1 overflow-hidden rounded-full bg-line"
          role="progressbar"
          aria-label="Сколько заданий решено"
          aria-valuenow={Math.round(progress * 100)}
          aria-valuetext={`решено ${Math.round(progress * queue.taskCount)} из ${queue.taskCount}`}
        >
          <div
            className="h-full origin-left bg-accent transition-transform duration-300 motion-reduce:transition-none"
            style={{ transform: `scaleX(${progress})` }}
          />
        </div>
      </div>

      {/* Обратная связь объявляется диктором: цветом и значком её не
          услышать, а без объявления ребёнок не узнаёт даже, верно ли
          ответил. */}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      <div ref={heading} tabIndex={-1} className="outline-none">
        {task.type === 'compare' ? (
          /** После ошибки показываются ОБА результата (раздел 5.7). Пока
           *  здесь стояли голые выражения, журнал записывал раскрытыми оба
           *  факта и обнулял по ним замеры на 12 часов вперёд — за
           *  раскрытие, которого не было. Плата за обучение взималась,
           *  обучение не выдавалось. */
          <div className="flex items-center justify-around gap-2">
            <span className={question}>
              {expr(task.fact)}
              {revealed ? ` = ${task.fact.answer}` : ''}
            </span>
            <span className={question}>
              {expr(task.partner!)}
              {revealed ? ` = ${task.partner!.answer}` : ''}
            </span>
          </div>
        ) : (
          <p className={question}>
            {expr(task.fact)} ={' '}
            {task.type === 'choice4' ? '?' : revealed ? task.fact.answer : task.presented}
          </p>
        )}
      </div>

      {task.type === 'choice4' && (
        <div className="grid grid-cols-2 gap-3">
          {task.options!.map((option) => (
            <AnswerButton
              key={option}
              mark={markOf(option)}
              disabled={blocked(option)}
              onClick={() => answer(option)}
              text={String(option)}
            />
          ))}
        </div>
      )}

      {task.type === 'truefalse' && (
        <div className="grid grid-cols-2 gap-3">
          {[true, false].map((value) => (
            <AnswerButton
              key={String(value)}
              mark={markOf(value)}
              disabled={blocked(value)}
              onClick={() => answer(value)}
              text={value ? 'Верно' : 'Неверно'}
            />
          ))}
        </div>
      )}

      {task.type === 'compare' && (
        <div className="grid grid-cols-3 gap-3">
          {(['>', '=', '<'] as const).map((rel) => (
            <AnswerButton
              key={rel}
              mark={markOf(rel)}
              disabled={blocked(rel)}
              onClick={() => answer(rel)}
              text={rel}
              spoken={RELATION_LABEL[rel]}
            />
          ))}
        </div>
      )}

      {/* Слово рядом с цветом и значком: у «верно/неверно» после раскрытия
          пометок на кнопках нет вовсе, и без подписи ребёнок не узнает, что
          ошибся. */}
      {queue.phase === 'feedbackWrong' && (
        <p className="m-0 text-center text-[1.05rem] font-semibold text-incorrect">Ошибка</p>
      )}
      {queue.phase === 'feedbackWrong' && (
        <button
          type="button"
          disabled={paused}
          className="mt-auto min-h-14 rounded-[0.85rem] bg-accent p-4 text-[1.1rem] font-semibold text-white"
          onClick={() => {
            if (paused) return;
            setGiven(null);
            onNext();
          }}
        >
          Дальше
        </button>
      )}
      {queue.phase === 'guided' && (
        <p className="m-0 text-[0.95rem] text-muted">Нажми верный ответ</p>
      )}
    </main>
  );
}
